/**
 * What the api tells us about the SECOND message a support request can raise
 * (kitecorp/transferify-api-service `SupportRequestReceipt`, kitecorp/transferify-ui#255).
 *
 * A withdrawal declaration produces two emails: the internal notice (whose failure is the 502 the
 * dialog already handles) and the consumer's own acknowledgement of receipt, which is best effort
 * because the declaration has already arrived by the time it is attempted. The success screen has to
 * say which of the two exist, so it reads this rather than guessing.
 */
export interface SupportRequestReceipt {
	/**
	 * Whether an acknowledgement of receipt reached the caller's own account address. Always `false`
	 * for an ordinary support request, which raises no acknowledgement at all — so this means "there is
	 * a receipt in your inbox", never "the request succeeded". Success is the resolved promise.
	 */
	acknowledgementSent: boolean;
}

/**
 * Reads a receipt out of a 200 body.
 *
 * Anything that is not literally `true` is read as "no acknowledgement", including a body from an api
 * that predates the field. That asymmetry is deliberate: the copy this drives tells a consumer their
 * statutory receipt is in their inbox, and claiming one we cannot confirm is the exact defect this
 * ticket removed from the previous success screen. Under-claiming costs a sentence; over-claiming
 * sends someone looking for a message that is not there.
 */
export function supportRequestReceiptOf(body: unknown): SupportRequestReceipt {
	const acknowledgementSent = typeof body === "object" && body !== null
		&& (body as {acknowledgementSent?: unknown}).acknowledgementSent === true;
	return {acknowledgementSent};
}
